import React from "react";
import { Link } from "react-router-dom";
import colors from "../../styles/colors";
import config from "../../utils/config";
import { useTranslation } from "react-i18next";
import i18n from "../../utils/i18n";

interface NavModalProps {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  hoveredLink: string | null;
  setHoveredLink: React.Dispatch<React.SetStateAction<string | null>>;
  currentTab: () => string;
}

const NavModal: React.FC<NavModalProps> = ({
  isOpen,
  setIsOpen,
  hoveredLink,
  setHoveredLink,
  currentTab,
}) => {
  const { t } = useTranslation();

  const handleMouseEnter = (tab: string) => {
    setHoveredLink(tab);
  };

  const handleMouseLeave = () => {
    setHoveredLink(null);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang); // Change language
    setIsOpen(false); // Close modal
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex flex-col justify-center items-center z-40 px-6"
      onClick={() => setIsOpen(false)}
    >
      {/* Divider */}
      <div
        className="absolute top-24 left-1/2 transform -translate-x-1/2 w-full max-w-[80%] h-[2px] opacity-80"
        style={{ backgroundColor: colors.lightCream }}
      ></div>

      {/* Menü linkek */}
      <div
        className="flex flex-col items-center space-y-8 md:space-y-12 font-light mt-20 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {["home", "about", "products", "portfolio", "contact"].map((tab) => (
          <Link
            key={tab}
            to={`/${tab === "home" ? "" : tab}`}
            className="relative lg:text-4xl text-2xl md:text-4xl font-serif transition-colors duration-300 text-center"
            style={{
              color:
                currentTab() === tab
                  ? colors.pantoneBrown
                  : hoveredLink === tab
                  ? colors.primaryGreen
                  : colors.lightCream,
            }}
            onMouseEnter={() => handleMouseEnter(tab)}
            onMouseLeave={handleMouseLeave}
            onClick={() => {
              setIsOpen(false);
              setHoveredLink(null);
            }}
          >
            {t(tab)}

            {/* Underline effect */}
            <span
              className="absolute left-0 bottom-0 w-0 h-[2px] transition-all duration-300"
              style={{
                width:
                  hoveredLink === tab || currentTab() === tab ? "100%" : "0%",
                backgroundColor:
                  currentTab() === tab
                    ? colors.pantoneBrown
                    : hoveredLink === tab
                    ? colors.primaryGreen
                    : "transparent",
              }}
            />
          </Link>
        ))}
      </div>

      {/* Contact Info & Language Selector - Responsive Layout */}
      <div className="absolute bottom-12 flex flex-col items-center lg:flex-row lg:justify-between w-full max-w-[80%]">
        {/* Email & Facebook */}
        <div className="flex flex-col items-center sm:flex-row sm:gap-8">
          <a
            href={`mailto:${config.email}`}
            className="text-white lg:text-xl text-lg md:text-xl font-serif transition-colors duration-300"
            style={{
              color: colors.lightCream,
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = colors.primaryGreen)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = colors.lightCream)
            }
          >
            {config.email}
          </a>
        </div>

        {/* Language Selector - One Line */}
        <div className="flex gap-4 sm:gap-6">
          {["ro", "en", "hu"].map((lang) => (
            <div
              key={lang}
              onClick={() => handleLanguageChange(lang)}
              className="cursor-pointer text-lg font-serif transition-colors duration-300"
              style={{
                color: colors.lightCream,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = colors.primaryGreen)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = colors.lightCream)
              }
            >
              {lang.toLowerCase()}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NavModal;
